/**
 * OSM-woodland vegetation (issue #56). Individual trees/shrubs are placed
 * INSIDE the real OSM landcover polygons of data/config/landcover.json — named
 * plantations read as forest, open fields stay open — replacing the roadside
 * random patches shipped in #44 (that pure roadside builder still lives in
 * vegetationPlacement.ts for reuse but is no longer the default source).
 *
 * The whole AOI is ~3,561 ha of woodland across 827 polygons, so naive
 * per-tree instancing across the AOI would be millions of instances. This
 * layer therefore runs a two-regime LOD (issue #17, low-power target):
 *
 *   - DISTANT / OVERVIEW: CanopyLayer draws ONE cheap flat tinted fill surface
 *     over every polygon at treetop height (vegetationCanopy.ts). All forest is
 *     representable across the whole AOI at near-zero cost — the boot overview
 *     (camera at y=250 over the 12 km AOI) reads as forest everywhere.
 *
 *   - NEAR CAMERA: individual instanced cross-plane tree cards are realised
 *     only for polygons within a bounded radius of the camera, under a GLOBAL
 *     live-instance cap, and released again when the camera moves away — so the
 *     whole AOI's trees are never instantiated at once. Density scales with the
 *     class and the adaptive quality tier.
 *
 * Hard perf rules honoured: instanced meshes share ONE material + atlas
 * (vegetationTexture.ts); a global cap bounds live tree instances; nothing runs
 * per render frame beyond the instancing/culling the renderer already does and
 * the existing tile-update tick; no per-instance materials. Purely cosmetic
 * scene furniture — NO bearing on any LOS result (hard constraint #1, guarded
 * by vegetation.guard.test.ts), and no elevation is invented: every instance
 * (and every canopy patch) is grounded on the DTM via the same collider
 * sampleGround that backs walk-mode ground lock (hard constraint #6).
 *
 * Card lighting (issue #57): alongside the atlas repaint (vegetationTexture.ts)
 * and the upward-blended card normals (vegetationPlacement.ts), the shared
 * material gets a view-dependent backlight glow term patched in via
 * onBeforeCompile — technique adapted from Braffolk/fable5-world-demo (MIT
 * licence, commit fd75fdb). It reuses MeshStandardMaterial's built-in
 * `vViewPosition` varying (view-space) rather than adding a new varying, so
 * it costs one extra uniform + a few ALU ops per fragment, no extra geometry
 * attributes and no per-frame allocation (the sun-direction uniform is a
 * single cached Vector3 mutated in place each frame, never reallocated).
 */
import {
  BufferAttribute,
  BufferGeometry,
  DoubleSide,
  Group,
  InstancedMesh,
  Matrix4,
  MeshStandardMaterial,
  Quaternion,
  Vector3,
} from 'three';
import type { GroundSampler } from '../camera/groundLock';
import type { LandcoverFile } from '../core/manifest';
import type { LocalXZ } from '../core/coordinates';
import {
  buildCardGeometry,
  landcoverRingToLocal,
  ringBounds,
  sampleAreaInstances,
  treeRowInstances,
  type AreaClass,
  type CardMode,
  type RingBounds,
  type VegetationInstance,
  type VegetationKind,
} from './vegetationPlacement';
import { atlasCellUvRect, makeVegetationTexture } from './vegetationTexture';
import type { TarmacClearance } from './tarmacClearance';
import { CanopyLayer } from './vegetationCanopy';
import { TreeMeshPools, type TreeSlotRef } from './trees/treePools';
import { poolFor } from './trees/treePoolMap';

/** Radius (m) around the camera within which a polygon's individual trees are
 * realised as instances. */
export const DEFAULT_NEAR_RADIUS_M = 750;
/** Retain instances + cached ground for a polygon out to this multiple of the
 * near radius, so a small camera wobble across the boundary doesn't thrash
 * (re-generate + re-sample) a polygon that's about to come back. */
const RETAIN_FACTOR = 1.7;
/** Default global cap on simultaneously-live tree instances (high tier). */
export const DEFAULT_TREE_INSTANCE_CAP = 6000;
/** Radius (m) within which tree instances are drawn as the full baked branched
 * MESH (issue #58) rather than a cheap billboard card. Kept much tighter than
 * the near radius so only the closest, most-scrutinised trees pay the mesh's
 * per-instance triangle cost; mid-distance trees stay billboards, distant
 * woodland stays the flat canopy sheet (issue #17, low-power target). */
export const DEFAULT_MESH_RADIUS_M = 170;

const Y_AXIS = new Vector3(0, 1, 0);
const ZERO_SCALE_MATRIX = new Matrix4().makeScale(0, 0, 0);

/** World-space direction the sun travels (matches lighting.ts's default rig:
 * a DirectionalLight at (-1500, 1800, 900) aimed at the origin), used as the
 * backlight glow's fallback when no real scene sun is wired in (e.g. in
 * tests). main.ts passes the ACTUAL rig's direction via VegetationLayerOptions
 * so the glow tracks the real sun rather than this default. */
export const DEFAULT_SUN_DIRECTION_WORLD = new Vector3(1500, -1800, -900).normalize();

/** Tint of the backlight glow term (rgb, 0..1) — green-yellow, as if sunlight
 * were passing through thin translucent leaves. */
export const BACKLIGHT_TINT = new Vector3(0.9, 1.0, 0.35);

/** Minimal structural subset of three.js's WebGLProgramParametersWithUniforms
 * that applyBacklightShader needs — kept narrow so it's constructible in a
 * plain unit test without a WebGLRenderer. */
export interface BacklightShaderTarget {
  uniforms: Record<string, { value: unknown }>;
  fragmentShader: string;
}

/**
 * Patches the shared vegetation material's compiled fragment shader with a
 * view-dependent backlight glow: `pow(max(dot(viewDir, sunDirView), 0), 5)`,
 * tinted green-yellow, added to totalEmissiveRadiance (issue #57 — foliage lit
 * from behind should glow, not read as a flat near-black silhouette).
 * `sunDirView` must already be the negated sun direction, in VIEW space (see
 * computeSunDirView) — the shader reuses MeshStandardMaterial's own
 * `vViewPosition` varying (view-space) for the surface-to-camera direction, so
 * both vectors are in the same space with no extra varying needed. Pure
 * string/uniform patching: testable without a WebGL context.
 */
export function applyBacklightShader(shader: BacklightShaderTarget, sunDirView: Vector3): void {
  shader.uniforms.uSunDirView = { value: sunDirView };
  shader.uniforms.uBacklightTint = { value: BACKLIGHT_TINT };
  shader.fragmentShader =
    `uniform vec3 uSunDirView;\nuniform vec3 uBacklightTint;\n` +
    shader.fragmentShader.replace(
      '#include <emissivemap_fragment>',
      `#include <emissivemap_fragment>
      {
        vec3 backlightViewDir = normalize( vViewPosition );
        float backlightTerm = pow( max( dot( backlightViewDir, uSunDirView ), 0.0 ), 5.0 );
        totalEmissiveRadiance += backlightTerm * uBacklightTint;
      }`,
    );
}

/** Transforms a world-space direction into the given camera's view space,
 * writing into `out` (reused every frame — no per-frame allocation). Camera
 * typed structurally so this stays testable with a plain Matrix4, no
 * WebGLRenderer/Camera construction required. */
export function computeSunDirView(
  negSunDirWorld: Vector3,
  cameraMatrixWorldInverse: Matrix4,
  out: Vector3,
): Vector3 {
  return out.copy(negSunDirWorld).transformDirection(cameraMatrixWorldInverse);
}

export interface VegetationLayerOptions {
  /** Scales planting density per polygon (issue #17 low tier: fewer trees). */
  densityScale?: number;
  /** Global cap on simultaneously-live tree instances (issue #17). */
  treeInstanceCap?: number;
  /** InstancedMesh allocation capacity for the billboard buckets, for a live
   * quality re-cap (issue #73). Defaults to treeInstanceCap. main.ts passes the
   * antialias bucket's maximum so a med→high live switch has headroom. */
  maxTreeInstanceCap?: number;
  /** Radius (m) around the camera within which trees are realised. */
  nearRadiusM?: number;
  /** World-space direction the sun travels (need not be pre-normalized);
   * drives the backlight glow term (issue #57). Defaults to
   * DEFAULT_SUN_DIRECTION_WORLD, matching lighting.ts's rig — main.ts passes
   * the real rig's direction so the glow tracks the actual scene sun. */
  sunDirection?: Vector3;
  /** Baked procedural-tree pools (issue #58). When provided (high tier, asset
   * loaded), tree instances within meshRadiusM render as the full branched
   * mesh; omit for the low tier / when the asset is missing, and every tree
   * stays a billboard card. */
  treePools?: TreeMeshPools;
  /** Radius (m) for the mesh LOD (see DEFAULT_MESH_RADIUS_M). */
  meshRadiusM?: number;
  /** Carriageway clearance (issue #79): drops trees whose seeded position falls
   * on the route ribbon or a tarmac road running through the wood. */
  clearance?: TarmacClearance;
}

function bucketKey(kind: VegetationKind, variant: number): string {
  return `${kind}:${variant}`;
}

interface Bucket {
  mesh: InstancedMesh;
  capacity: number;
  /** Next never-used index; also the high-water mark → mesh.count. */
  nextFresh: number;
  /** Freed indices available for reuse (holes below nextFresh). */
  freed: number[];
  dirty: boolean;
}

/** A claimed render slot for one instance: either a billboard-card bucket slot
 * (the default / far / low-tier representation) or a baked-mesh pool slot (issue
 * #58 near-LOD). The instance's representation is chosen per polygon by camera
 * distance (see wantRep). */
type SlotRef =
  | { rep: 'card'; key: string; index: number }
  | { rep: 'mesh'; poolRef: TreeSlotRef };

/** Whether a polygon's realised representation this activation is card or mesh. */
type Rep = 'card' | 'mesh';

interface FeatureRec {
  seed: number;
  /** Area ring (closed) or tree_row polyline (open), in local x/z. */
  geom: LocalXZ[];
  area: AreaClass | null; // null => tree_row line
  bounds: RingBounds;
  active: boolean;
  /** Lazily generated & retained while within the retain radius. */
  instances?: VegetationInstance[];
  resolvedY?: Array<number | null>;
  slot?: Array<SlotRef | null>;
  attemptGen?: number[];
}

/** Only tree-bearing polygons (forest/wood areas and tree_row lines) are
 * eligible for the mesh LOD; scrub is shrubs, always billboard cards. */
function isTreeRec(rec: FeatureRec): boolean {
  return rec.area === 'forest' || rec.area === 'wood' || rec.area === null;
}

/** Nearest-point distance (m) from (cx,cz) to an axis-aligned bounds. */
function boundsDistance(b: RingBounds, cx: number, cz: number): number {
  const dx = Math.max(b.minX - cx, 0, cx - b.maxX);
  const dz = Math.max(b.minZ - cz, 0, cz - b.maxZ);
  return Math.hypot(dx, dz);
}

export class VegetationLayer {
  readonly group = new Group();
  private readonly material: MeshStandardMaterial;
  private readonly canopy: CanopyLayer;
  private readonly features: FeatureRec[] = [];
  private readonly buckets = new Map<string, Bucket>();
  private readonly densityScale: number;
  /** Active billboard-card cap (mutable for a live quality re-cap, issue #73). */
  private cap: number;
  /** Bucket InstancedMesh allocation capacity (fixed); active cap ≤ it. */
  private readonly allocCap: number;
  private readonly nearRadius: number;
  private readonly retainRadius: number;
  private readonly treePools?: TreeMeshPools;
  private readonly meshRadius: number;
  private readonly meshRetainRadius: number;
  private readonly clearance?: TarmacClearance;
  private liveCount = 0;
  private fallbackGeneration = 0;
  /** Trees currently on the baked-mesh LOD, keyed `${seed}:${index}` → their
   * polygon + instance index. Walked (≤ mesh cap entries) each reconcile to
   * demote the ones a nearer tree has displaced (issue #82) — so this never
   * grows past the mesh cap and demotion never scans the whole AOI. */
  private readonly meshHolders = new Map<string, { rec: FeatureRec; i: number }>();
  /** Polygons the mesh pass promoted/demoted a tree in this reconcile; the
   * card-fill pass revisits them so their remaining non-mesh trees still get a
   * card even though the mesh pass already marked them active (issue #82). */
  private readonly _meshTouched = new Set<FeatureRec>();
  // Reused scratch to avoid per-instance allocation while writing matrices.
  private readonly _pos = new Vector3();
  private readonly _quat = new Quaternion();
  private readonly _scl = new Vector3();
  private readonly _mat = new Matrix4();
  // Backlight glow scratch (issue #57): negSunDirWorld is fixed at
  // construction (the sun never moves at runtime); sunDirView is the same
  // Vector3 re-used every frame by onBeforeRender, and is the exact object
  // captured by the shader uniform in onBeforeCompile, so mutating it here
  // is all that's needed to keep the GPU uniform in sync — no reallocation.
  private readonly _negSunDirWorld: Vector3;
  private readonly _sunDirView: Vector3;

  constructor(landcover: LandcoverFile, options: VegetationLayerOptions = {}) {
    this.group.name = 'vegetation';
    this.densityScale = options.densityScale ?? 1;
    this.cap = options.treeInstanceCap ?? DEFAULT_TREE_INSTANCE_CAP;
    this.allocCap = Math.max(this.cap, options.maxTreeInstanceCap ?? this.cap);
    this.nearRadius = options.nearRadiusM ?? DEFAULT_NEAR_RADIUS_M;
    this.retainRadius = this.nearRadius * RETAIN_FACTOR;
    this.treePools = options.treePools;
    this.meshRadius = options.meshRadiusM ?? DEFAULT_MESH_RADIUS_M;
    this.meshRetainRadius = this.meshRadius * RETAIN_FACTOR;
    this.clearance = options.clearance;
    if (this.treePools) this.group.add(this.treePools.group);
    this._negSunDirWorld = (options.sunDirection ?? DEFAULT_SUN_DIRECTION_WORLD).clone().normalize().negate();
    this._sunDirView = this._negSunDirWorld.clone(); // sane default before the first render frame

    this.material = new MeshStandardMaterial({
      map: makeVegetationTexture(),
      alphaTest: 0.5,
      transparent: false,
      side: DoubleSide,
      roughness: 1,
      metalness: 0,
    });
    this.material.onBeforeCompile = (shader) => applyBacklightShader(shader, this._sunDirView);
    this.material.onBeforeRender = (_renderer, _scene, camera) => {
      computeSunDirView(this._negSunDirWorld, camera.matrixWorldInverse, this._sunDirView);
    };

    // Share the SAME near radius so the overview canopy hides exactly where the
    // near-camera instanced trees take over (issue #56): no floating sheet over
    // the real trees, and a seamless crossfade at the boundary.
    this.canopy = new CanopyLayer(landcover.features, { nearRadiusM: this.nearRadius });
    this.group.add(this.canopy.group);

    landcover.features.forEach((f, i) => {
      const geom = landcoverRingToLocal(f.geometry);
      if (geom.length < 2) return;
      const area: AreaClass | null =
        f.landcover_class === 'tree_row' ? null : (f.landcover_class as AreaClass);
      // Distinct seed per feature index so neighbouring woods don't repeat.
      this.features.push({ seed: i + 1, geom, area, bounds: ringBounds(geom), active: false });
    });
  }

  /** Number of billboard-card instances currently claimed (live). */
  get liveInstanceCount(): number {
    return this.liveCount;
  }

  /** Number of trees currently drawn as the baked branched mesh (issue #58). */
  get treeMeshInstanceCount(): number {
    return this.treePools?.liveInstanceCount ?? 0;
  }

  /** Total instanced draw calls from the tree-mesh pools (bark + foliage). */
  get treeMeshDrawCalls(): number {
    return this.treePools?.drawCallCount ?? 0;
  }

  /** Number of area polygons whose canopy fill has not yet grounded. */
  get canopyUnresolvedCount(): number {
    return this.canopy.unresolvedCount;
  }

  /** Active billboard-card instance cap (issue #73 live quality re-cap). */
  get instanceCap(): number {
    return this.cap;
  }

  /** Active baked-mesh tree cap (issue #73); 0 when no pools are wired. */
  get treeMeshCap(): number {
    return this.treePools?.capacity ?? 0;
  }

  /**
   * Changes the live billboard-card instance cap (issue #73). Clamped to the
   * bucket allocation capacity. Releases every active polygon's slots so the
   * next `update()` re-reconciles nearest-first under the new cap, reusing
   * cached instances + ground (no regeneration, no re-raycast) — the same path a
   * polygon takes when it re-enters the near radius. Off the render loop.
   */
  setInstanceCap(cap: number): void {
    const clamped = Math.max(0, Math.min(Math.floor(cap), this.allocCap));
    if (clamped === this.cap) return;
    this.cap = clamped;
    this.releaseAllActive();
  }

  /**
   * Changes the live baked-mesh tree cap (issue #73), forwarding to the tree
   * pools and re-reconciling as {@link setInstanceCap} does. No-op when no pools
   * are wired (low tier / missing baked asset). Because a polygon's mesh slots
   * are released and re-claimed here, the pools' own capacity change never needs
   * to trim slots itself.
   */
  setTreeMeshCap(cap: number): void {
    if (!this.treePools) return;
    if (cap === this.treePools.capacity) return;
    this.treePools.setCap(cap);
    this.releaseAllActive();
  }

  /** Deactivates every active polygon (frees its card + mesh slots) and flushes,
   * so the next update() re-reconciles under the current caps. */
  private releaseAllActive(): void {
    for (const rec of this.features) if (rec.active) this.deactivate(rec);
    this.flushBuckets();
    this.treePools?.flush();
  }

  /**
   * Advances both LOD regimes. Grounds still-unresolved canopy polygons and
   * near-camera tree instances (up to maxSamples raycasts total this call,
   * split between the two), reconciles which polygons are realised around the
   * camera, and reports whether more ground-sampling work remains next tick
   * (same "call me again" contract as RoadWallsLayer / CanopyLayer).
   */
  update(
    sampleGround: GroundSampler,
    maxSamples = Infinity,
    generation?: number,
    camera?: { x: number; z: number },
  ): boolean {
    const gen = generation ?? (this.fallbackGeneration -= 1);
    const finite = maxSamples !== Infinity;

    // 1. Reconcile which polygons are realised around the camera (no raycasts).
    if (camera) this.reconcile(camera.x, camera.z);

    // 2. Ground the near-camera instances — the close-up detail the user looks
    //    directly at — but CAP them at half the budget so a big fresh wood full
    //    of trees can never starve the distant canopy. The overview canopy is no
    //    longer a flat sheet resolved in ONE sample; draping it per vertex is
    //    many raycasts (issue #56), so it needs a guaranteed share. Within that
    //    half, a contended budget grounds the nearest pending instances first
    //    (issue #97), so the camera's close trees settle before distant ones.
    const half = finite ? Math.ceil(maxSamples / 2) : Infinity;
    const inst = this.groundInstances(sampleGround, half, gen, camera);
    this.flushBuckets();
    this.treePools?.flush();

    // 3. Distant canopy: ground the remaining vertices — at least half the
    //    budget, up to the whole budget when the near instances are idle (e.g.
    //    the boot overview) — then reconcile its per-camera crossfade /
    //    near-radius hiding. Always called (budget 0 once fully grounded) so the
    //    crossfade keeps tracking the camera every tick.
    const canopyNeedsGround = this.canopy.unresolvedCount > 0;
    const canopyBudget = canopyNeedsGround ? (finite ? maxSamples - inst.used : Infinity) : 0;
    const canopyExhausted = this.canopy.update(sampleGround, canopyBudget, generation, camera);

    return inst.exhausted || canopyExhausted;
  }

  /** Reconciles what's realised around the camera in three pure slot-bookkeeping
   * passes (no ground sampling here):
   *  A) release polygons leaving the near radius, and drop far-away caches;
   *  B) assign the baked-mesh LOD to the globally-closest eligible trees, by
   *     per-tree camera distance across every polygon at once (issue #82);
   *  C) card-fill the remaining near trees, nearest-bounds-first under the cap. */
  private reconcile(cx: number, cz: number): void {
    // A. Release out-of-range polygons first, freeing slots (and cap) for nearer
    // ones this call.
    for (const rec of this.features) {
      const d = boundsDistance(rec.bounds, cx, cz);
      if (rec.active && d > this.nearRadius) this.deactivate(rec);
      if (!rec.active && d > this.retainRadius) this.dropCache(rec);
    }
    // B. Per-tree mesh LOD, ordered globally by camera distance.
    this.assignMeshSlots(cx, cz);
    // C. Card-fill: polygons newly entering the near radius, plus any the mesh
    // pass touched (their non-mesh trees still need a card), nearest-bounds-first
    // so the card cap spends on the closest woodland.
    const eligible = this.features
      .map((rec) => ({ rec, d: boundsDistance(rec.bounds, cx, cz) }))
      .filter((e) => e.d <= this.nearRadius && (!e.rec.active || this._meshTouched.has(e.rec)))
      .sort((a, b) => a.d - b.d);
    for (const e of eligible) {
      if (this.liveCount >= this.cap) break;
      this.activate(e.rec);
    }
  }

  /**
   * Assigns the baked-mesh LOD (issue #58) to the globally-closest eligible
   * trees under the mesh cap, ordered by *per-tree* camera distance across every
   * polygon at once (issue #82). No nearer tree is ever left a billboard card
   * while a farther tree holds a mesh slot, regardless of which polygon each
   * belongs to or the order they're stored in. When the cap is contended and a
   * closer tree becomes eligible, a farther mesh-holder is demoted back to a
   * card so the closer tree can take the freed slot. Ties break deterministically
   * on (seed, index) so the choice never flickers tick-to-tick.
   *
   * Cost is bounded by the near-mesh tree population, never the whole AOI: only
   * polygons whose bounds fall within the mesh retain radius are scanned for
   * candidates (a tree can be within meshRadius only if its polygon's bounds
   * are), and demotion walks just the current mesh-holders (≤ mesh cap). One
   * sort of that bounded candidate set per tick, on the existing tile-update tick
   * — no per-render-frame work and no AOI-wide sort (the perf trap from #80).
   */
  private assignMeshSlots(cx: number, cz: number): void {
    this._meshTouched.clear();
    const pools = this.treePools;
    if (!pools) return;
    const meshCap = pools.capacity;
    if (meshCap <= 0) {
      // No mesh budget (e.g. after a live re-cap to 0): demote any stragglers.
      for (const [, h] of [...this.meshHolders]) this.demoteMesh(h.rec, h.i);
      return;
    }
    const r2 = this.meshRadius * this.meshRadius;
    const rr2 = this.meshRetainRadius * this.meshRetainRadius;
    // Collect every mesh-eligible tree. A tree already on the mesh keeps its
    // eligibility out to the retain radius (hysteresis) so it doesn't thrash
    // against the boundary as the camera jitters.
    const candidates: Array<{ rec: FeatureRec; i: number; d2: number }> = [];
    for (const rec of this.features) {
      if (!isTreeRec(rec)) continue;
      if (boundsDistance(rec.bounds, cx, cz) > this.meshRetainRadius) continue;
      this.ensureInstances(rec);
      const instances = rec.instances!;
      const slot = rec.slot!;
      for (let i = 0; i < instances.length; i++) {
        const dx = instances[i].x - cx;
        const dz = instances[i].z - cz;
        const d2 = dx * dx + dz * dz;
        const isMesh = slot[i]?.rep === 'mesh';
        if (d2 <= (isMesh ? rr2 : r2)) candidates.push({ rec, i, d2 });
      }
    }
    // The mesh cap goes to the globally-closest candidates; the (seed, index)
    // tie-break keeps the selection stable when distances are equal.
    candidates.sort((a, b) => a.d2 - b.d2 || a.rec.seed - b.rec.seed || a.i - b.i);
    const winners = candidates.slice(0, meshCap);
    const winnerKeys = new Set<string>();
    for (const w of winners) winnerKeys.add(`${w.rec.seed}:${w.i}`);
    // Demote holders that lost their slot (out of range, or edged out by a
    // nearer tree) BEFORE promoting, so the freed budget is available.
    for (const [key, h] of [...this.meshHolders]) {
      if (!winnerKeys.has(key)) this.demoteMesh(h.rec, h.i);
    }
    // Promote winners not already on the mesh.
    for (const w of winners) {
      const key = `${w.rec.seed}:${w.i}`;
      if (this.meshHolders.has(key)) continue;
      this.promoteMesh(w.rec, w.i, key);
    }
  }

  /** Frees a tree's mesh slot and gives it a billboard card instead (issue #82
   * demotion). Reuses the existing claim/free machinery — no polygon rebuild. */
  private demoteMesh(rec: FeatureRec, i: number): void {
    const ref = rec.slot?.[i];
    if (!ref || ref.rep !== 'mesh') return;
    this.freeSlot(ref);
    this.meshHolders.delete(`${rec.seed}:${i}`);
    const inst = rec.instances![i];
    const card = this.claimSlot('card', inst);
    rec.slot![i] = card;
    const y = rec.resolvedY![i];
    if (card && y !== null) this.writeMatrix(card, inst, y);
    this._meshTouched.add(rec);
  }

  /** Claims a mesh slot for a tree, freeing whatever card slot it held (issue
   * #82 promotion). No-op if the tree's species pool is unavailable/full. */
  private promoteMesh(rec: FeatureRec, i: number, key: string): void {
    const inst = rec.instances![i];
    const mesh = this.claimSlot('mesh', inst);
    if (!mesh) return;
    const old = rec.slot![i];
    if (old) this.freeSlot(old);
    rec.slot![i] = mesh;
    this.meshHolders.set(key, { rec, i });
    rec.active = true; // ensure grounding covers it even if card-fill breaks early
    const y = rec.resolvedY![i];
    if (y !== null) this.writeMatrix(mesh, inst, y);
    this._meshTouched.add(rec);
  }

  private ensureInstances(rec: FeatureRec): void {
    if (rec.instances) return;
    const opts = { densityScale: this.densityScale, clearance: this.clearance };
    rec.instances =
      rec.area === null ? treeRowInstances(rec.geom, rec.seed, opts) : sampleAreaInstances(rec.geom, rec.area, rec.seed, opts);
    const n = rec.instances.length;
    rec.resolvedY = new Array<number | null>(n).fill(null);
    rec.slot = new Array<SlotRef | null>(n).fill(null);
    rec.attemptGen = new Array<number>(n).fill(Number.NaN);
  }

  /** Card-fills a polygon: claims a billboard-card slot for every tree that
   * doesn't already hold one (mesh winners keep their mesh slot), under the
   * global card cap. Idempotent — re-running skips already-slotted trees. The
   * baked-mesh LOD is assigned separately, globally, by {@link assignMeshSlots}. */
  private activate(rec: FeatureRec): void {
    this.ensureInstances(rec);
    const instances = rec.instances!;
    const slot = rec.slot!;
    const resolvedY = rec.resolvedY!;
    for (let i = 0; i < instances.length; i++) {
      if (slot[i]) continue; // already claimed (mesh winner, or carded on a prior tick)
      if (this.liveCount >= this.cap) break; // card cap spent
      const ref = this.claimSlot('card', instances[i]);
      if (!ref) break;
      slot[i] = ref;
      // If this instance's ground is already cached (retained across a previous
      // deactivation), place it immediately; otherwise it grounds in groundInstances.
      const y = resolvedY[i];
      if (y !== null) this.writeMatrix(ref, instances[i], y);
    }
    rec.active = true;
  }

  private deactivate(rec: FeatureRec): void {
    if (rec.slot) {
      for (let i = 0; i < rec.slot.length; i++) {
        const ref = rec.slot[i];
        if (!ref) continue;
        if (ref.rep === 'mesh') this.meshHolders.delete(`${rec.seed}:${i}`);
        this.freeSlot(ref);
        rec.slot[i] = null;
      }
    }
    rec.active = false;
  }

  private dropCache(rec: FeatureRec): void {
    rec.instances = undefined;
    rec.resolvedY = undefined;
    rec.slot = undefined;
    rec.attemptGen = undefined;
  }

  /**
   * Grounds still-unresolved near-camera instances, up to maxSamples raycasts.
   * Gathers this generation's pending instances across every active polygon,
   * then spends the budget nearest-camera-first (issue #97) — by actual
   * per-instance distance, across polygon boundaries — so a contended tick
   * resolves the trees the camera is looking at before distant ones, matching
   * the discipline reconcile()/assignMeshSlots already apply. Ties break on
   * (seed, index) so the order can't flicker tick-to-tick with a still camera.
   * With no camera the walk stays in file/instance order. The
   * generation/attemptGen dedupe and the used/exhausted contract are unchanged;
   * only the iteration order is. The pending array is bounded by the pending
   * count (shrinks as instances ground), built on the tile-update tick — never
   * per render frame.
   */
  private groundInstances(
    sampleGround: GroundSampler,
    maxSamples: number,
    gen: number,
    camera?: { x: number; z: number },
  ): { used: number; exhausted: boolean } {
    const pending: Array<{ rec: FeatureRec; i: number; d2: number }> = [];
    for (const rec of this.features) {
      if (!rec.active || !rec.instances) continue;
      const instances = rec.instances;
      const slot = rec.slot!;
      const resolvedY = rec.resolvedY!;
      const attemptGen = rec.attemptGen!;
      for (let i = 0; i < instances.length; i++) {
        if (!slot[i] || resolvedY[i] !== null || attemptGen[i] === gen) continue;
        let d2 = 0;
        if (camera) {
          const dx = instances[i].x - camera.x;
          const dz = instances[i].z - camera.z;
          d2 = dx * dx + dz * dz;
        }
        pending.push({ rec, i, d2 });
      }
    }
    if (camera) pending.sort((a, b) => a.d2 - b.d2 || a.rec.seed - b.rec.seed || a.i - b.i);

    let samples = 0;
    let exhausted = false;
    for (const p of pending) {
      if (samples >= maxSamples) {
        exhausted = true;
        break;
      }
      samples += 1;
      const inst = p.rec.instances![p.i];
      p.rec.attemptGen![p.i] = gen;
      const y = sampleGround(inst.x, inst.z);
      if (y === null) continue;
      p.rec.resolvedY![p.i] = y;
      this.writeMatrix(p.rec.slot![p.i]!, inst, y);
    }
    return { used: samples, exhausted };
  }

  private createBucket(key: string): Bucket {
    const sep = key.indexOf(':');
    const kind = key.slice(0, sep) as VegetationKind;
    const variant = Number(key.slice(sep + 1));
    const mode: CardMode = kind === 'groundcover' ? 'single' : 'cross';
    const arrays = buildCardGeometry(mode, atlasCellUvRect(kind, variant));
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(new Float32Array(arrays.positions), 3));
    geometry.setAttribute('normal', new BufferAttribute(new Float32Array(arrays.normals), 3));
    geometry.setAttribute('uv', new BufferAttribute(new Float32Array(arrays.uvs), 2));
    geometry.setIndex(arrays.indices);
    // Capacity == the global cap: any single bucket may, in the worst case,
    // hold every live instance (e.g. an all-conifer forest), while the global
    // liveCount is what actually bounds the total across buckets.
    const mesh = new InstancedMesh(geometry, this.material, this.allocCap);
    mesh.name = `vegetation-${key}`;
    mesh.castShadow = true;
    mesh.count = 0; // nothing drawn until slots are claimed
    this.group.add(mesh);
    const bucket: Bucket = { mesh, capacity: this.allocCap, nextFresh: 0, freed: [], dirty: false };
    this.buckets.set(key, bucket);
    return bucket;
  }

  /** Claims a slot in the requested representation, or null if that
   * representation's cap is spent. Mesh slots live in the tree pools; card
   * slots in the billboard buckets (bounded by the global card cap). */
  private claimSlot(rep: Rep, inst: VegetationInstance): SlotRef | null {
    if (rep === 'mesh') {
      const pools = this.treePools;
      if (!pools) return null;
      const sel = poolFor(inst.variant, inst.x, inst.z, pools.speciesCount, pools.structuralVariants);
      const poolRef = pools.claimSlot(sel.speciesIndex, sel.variantIndex);
      return poolRef ? { rep: 'mesh', poolRef } : null;
    }
    if (this.liveCount >= this.cap) return null;
    const key = bucketKey(inst.kind, inst.variant);
    const bucket = this.buckets.get(key) ?? this.createBucket(key);
    let index: number;
    if (bucket.freed.length > 0) {
      index = bucket.freed.pop()!;
    } else if (bucket.nextFresh < bucket.capacity) {
      index = bucket.nextFresh++;
      bucket.mesh.count = bucket.nextFresh; // grow the drawn range to the high-water mark
    } else {
      return null;
    }
    bucket.mesh.setMatrixAt(index, ZERO_SCALE_MATRIX); // invisible until grounded
    bucket.dirty = true;
    this.liveCount += 1;
    return { rep: 'card', key, index };
  }

  private freeSlot(ref: SlotRef): void {
    if (ref.rep === 'mesh') {
      this.treePools?.freeSlot(ref.poolRef);
      return;
    }
    const bucket = this.buckets.get(ref.key);
    if (!bucket) return;
    bucket.mesh.setMatrixAt(ref.index, ZERO_SCALE_MATRIX);
    bucket.freed.push(ref.index);
    bucket.dirty = true;
    this.liveCount -= 1;
  }

  private writeMatrix(ref: SlotRef, inst: VegetationInstance, y: number): void {
    if (ref.rep === 'mesh') {
      this.treePools?.writeMatrix(ref.poolRef, inst.x, y, inst.z, inst.rotationY, inst.scale);
      return;
    }
    const bucket = this.buckets.get(ref.key);
    if (!bucket) return;
    this._pos.set(inst.x, y, inst.z);
    this._quat.setFromAxisAngle(Y_AXIS, inst.rotationY);
    this._scl.set(inst.scale, inst.scale, inst.scale);
    this._mat.compose(this._pos, this._quat, this._scl);
    bucket.mesh.setMatrixAt(ref.index, this._mat);
    bucket.dirty = true;
  }

  private flushBuckets(): void {
    for (const bucket of this.buckets.values()) {
      if (bucket.dirty) {
        bucket.mesh.instanceMatrix.needsUpdate = true;
        bucket.dirty = false;
      }
    }
  }
}
